import { useState, useEffect, useRef } from "react";
import { api } from "./api";
import Login from "./Login";

const QUICK_MESSAGES = [
  { label: "ayuda", msg: "ayuda" },
  { label: "saldo", msg: "saldo" },
  { label: "depositar", msg: "depositar" },
  { label: "retirar $50", msg: "retirar $50" },
  { label: "mandá $20", msg: "mandá $20 a +5491112345678" },
  { label: "mandá 50k ARS", msg: "mandá 50000 ARS a +5491112345678" },
  { label: "pedile $30", msg: "pedile $30 a +5491198765432" },
  { label: "sí", msg: "sí" },
  { label: "cancelar", msg: "cancelar" },
];

const STATE_COLORS = {
  IDLE:             "#34d399",
  CONFIRM_TRANSFER: "#fbbf24",
  CONFIRM_REQUEST:  "#fbbf24",
  CONFIRM_WITHDRAW: "#fbbf24",
  AWAITING_OTP:     "#a78bfa",
  EXECUTING:        "#f87171",
};

function InteractiveMessage({ msg, onAction }) {
  const btnStyle = {
    display: "block", width: "100%", marginTop: 6,
    padding: "6px 10px", fontSize: 12, textAlign: "center",
    background: "transparent", border: "1px solid #34d399",
    borderRadius: 4, color: "#34d399", cursor: "pointer",
    fontFamily: "monospace",
  };

  if (msg.type === "buttons") {
    return (
      <div>
        <div style={{ marginBottom: 4 }}>{msg.body}</div>
        {(msg.buttons || []).map(b => (
          <button key={b.id} style={btnStyle} onClick={() => onAction(b.title, b.id)}>
            {b.title}
          </button>
        ))}
      </div>
    );
  }

  if (msg.type === "list") {
    return (
      <div>
        <div style={{ marginBottom: 4 }}>{msg.body}</div>
        {(msg.items || []).map(item => (
          <button key={item.id} style={{ ...btnStyle, textAlign: "left", border: "1px solid #1a2a1a" }}
            onClick={() => onAction(item.title, item.id)}>
            <span style={{ color: "#34d399" }}>›</span> {item.title}
            {item.description ? <span style={{ color: "#4a6a4a", marginLeft: 6, fontSize: 10 }}>{item.description}</span> : null}
          </button>
        ))}
      </div>
    );
  }

  if (msg.type === "card") {
    return (
      <div>
        {msg.title && <div style={{ fontWeight: "bold", marginBottom: 4, color: "#e8f5e8" }}>{msg.title}</div>}
        <div style={{ marginBottom: (msg.buttons || []).length ? 4 : 0 }}>{msg.body}</div>
        {(msg.buttons || []).map(b => (
          <button key={b.id} style={btnStyle} onClick={() => onAction(b.title, b.id)}>
            {b.title}
          </button>
        ))}
      </div>
    );
  }

  return <div style={{ color: "#4a6a4a", fontSize: 11 }}>[interactive: {msg.type}]</div>;
}

export default function Simulator() {
  const [authed, setAuthed] = useState(() => !!api.getToken());
  const [phone, setPhone] = useState("+59899111111");
  const [messages, setMessages] = useState([
    { from: "bot", text: "👋 Hola! Soy el bot de Zer0. Escribí *ayuda* para ver qué puedo hacer.", time: new Date() }
  ]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [session, setSession] = useState({});
  const [otpCode, setOtpCode] = useState(null);
  const chatRef = useRef(null);
  const inputRef = useRef(null);

  // Scroll al último mensaje
  useEffect(() => {
    if (chatRef.current) {
      chatRef.current.scrollTop = chatRef.current.scrollHeight;
    }
  }, [messages, loading]);

  // Foco automático en el input
  useEffect(() => {
    if (inputRef.current) {
      inputRef.current.focus();
    }
  }, [messages, loading]);

  // Polling de sesión cada 2 segundos
  useEffect(() => {
    if (!authed) return;
    const fetchSession = async () => {
      try {
        const data = await api.getBotSession(phone);
        setSession(data);
        if (data.notifications && data.notifications.length > 0) {
          setMessages(m => [
            ...m,
            ...data.notifications.map(text => ({ from: "bot", text, time: new Date() })),
          ]);
        }
      } catch (e) {}
    };
    fetchSession();
    const interval = setInterval(fetchSession, 2000);
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phone, authed]);

  const sendMessage = async (text, interactivePayload = null) => {
    if (!text.trim() || loading) return;
    const userMsg = { from: "user", text, time: new Date() };
    setMessages(m => [...m, userMsg]);
    setInput("");
    setLoading(true);

    try {
      const data = await api.simulateBotMessage(phone, text, interactivePayload);
      const botText = data.response;
      const interactive = data.interactive;

      // Interactive messages (buttons, lists, cards) — bot sent directly, response is null
      if (interactive && interactive.length > 0) {
        for (const msg of interactive) {
          if (msg.type === "text") {
            setMessages(m => [...m, { from: "bot", text: msg.body, time: new Date() }]);
          } else {
            setMessages(m => [...m, { from: "bot", interactive: msg, time: new Date() }]);
          }
        }
      }

      if (botText) {
        const otpMatch = botText.match(/\[SIMULADOR\] Tu código es: (\d{6})/);
        if (otpMatch) {
          setOtpCode(otpMatch[1]);
          const cleanText = botText.replace(/\*\[SIMULADOR\] Tu código es: \d{6}\*\n\n/, "");
          setMessages(m => [...m, { from: "bot", text: cleanText, time: new Date() }]);
        } else {
          if (!botText.includes("código") && !botText.includes("OTP")) {
            setOtpCode(null);
          }
          setMessages(m => [...m, { from: "bot", text: botText, time: new Date() }]);
        }
      }
    } catch (e) {
      setMessages(m => [...m, { from: "bot", text: "❌ Error conectando con el bot.", time: new Date() }]);
    }
    setLoading(false);

    // Refocus input después de enviar
    setTimeout(() => inputRef.current?.focus(), 100);
  };

  const resetChat = () => {
    setMessages([{ from: "bot", text: "👋 Hola! Soy el bot de Zer0. Escribí *ayuda* para ver qué puedo hacer.", time: new Date() }]);
    setOtpCode(null);
    setSession({});
    setTimeout(() => inputRef.current?.focus(), 100);
  };

  const resetDemo = async () => {
    if (!window.confirm("¿Resetear los saldos demo a sus valores iniciales? Esto afecta a todos los usuarios demo, no solo a este número.")) return;
    try {
      await api.resetBotDemo();
      setMessages(m => [...m, { from: "bot", text: "🔄 Saldos demo reseteados a valores iniciales.", time: new Date() }]);
    } catch (e) {
      setMessages(m => [...m, { from: "bot", text: "❌ Error al resetear saldos demo.", time: new Date() }]);
    }
  };

  const formatTime = (d) => new Date(d).toLocaleTimeString("es-UY", { hour: "2-digit", minute: "2-digit" });

  const formatText = (text) => {
    return text.split("\n").map((line, i) => {
      const parts = line.split(/(\*[^*]+\*)/g);
      return (
        <div key={i} style={{ minHeight: line === "" ? 8 : "auto" }}>
          {parts.map((part, j) =>
            part.startsWith("*") && part.endsWith("*")
              ? <strong key={j}>{part.slice(1, -1)}</strong>
              : part
          )}
        </div>
      );
    });
  };

  const currentState = session.state || "IDLE";
  const stateColor = STATE_COLORS[currentState] || "#6b7280";
  const ctx = session.context || {};

  if (!authed) return <Login onLogin={() => setAuthed(true)} />;

  return (
    <div style={{ height: "100dvh", background: "#080c08", fontFamily: "'DM Mono', monospace", color: "#c8e6c8", display: "flex", flexDirection: "column", overflow: "hidden" }}>
      <style>{`
        @import url('/fonts/fonts.css');
        html, body { margin: 0; padding: 0; height: 100%; overflow: hidden; }
        * { box-sizing: border-box; }
        ::-webkit-scrollbar { width: 4px; background: #0d120d; }
        ::-webkit-scrollbar-thumb { background: #1a2a1a; }
        button:hover { opacity: 0.85; }
        @media (max-width: 768px) {
          .side-panel { display: none !important; }
          .quick-messages { flex-wrap: nowrap !important; overflow-x: auto !important; }
          .quick-messages::-webkit-scrollbar { height: 2px; }
        }
      `}</style>

      {/* Header */}
      <div style={{ borderBottom: "1px solid #1a2a1a", padding: "0 16px", display: "flex", alignItems: "center", height: 52, flexShrink: 0, gap: 12 }}>
        <div style={{ fontSize: 10, letterSpacing: 4, color: "#34d399", textTransform: "uppercase" }}>ZER0</div>
        <div style={{ fontSize: 10, color: "#3a5a3a" }}>·</div>
        <div style={{ fontSize: 10, color: "#4a6a4a", letterSpacing: 2, textTransform: "uppercase" }}>Simulador</div>
        <div style={{ marginLeft: "auto", display: "flex", alignItems: "center", gap: 8 }}>
          <input
            value={phone}
            onChange={e => setPhone(e.target.value)}
            onBlur={resetChat}
            onKeyDown={e => e.key === "Enter" && e.target.blur()}
            style={{ background: "#111811", border: "1px solid #1a2a1a", borderRadius: 2, padding: "4px 8px", color: "#c8e6c8", fontSize: 11, outline: "none", width: 140, fontFamily: "monospace" }}
          />
          <button onClick={resetDemo}
            style={{ padding: "4px 10px", fontSize: 9, letterSpacing: 2, background: "transparent", border: "1px solid #1a2a1a", borderRadius: 2, color: "#4a6a4a", cursor: "pointer", textTransform: "uppercase" }}>
            Reset Demo
          </button>
          <button onClick={resetChat}
            style={{ padding: "4px 10px", fontSize: 9, letterSpacing: 2, background: "transparent", border: "1px solid #1a2a1a", borderRadius: 2, color: "#4a6a4a", cursor: "pointer", textTransform: "uppercase" }}>
            Reset Chat
          </button>
          <button onClick={() => { api.logout(); setAuthed(false); }}
            style={{ padding: "4px 10px", fontSize: 9, letterSpacing: 2, background: "transparent", border: "1px solid #1a2a1a", borderRadius: 2, color: "#4a6a4a", cursor: "pointer", textTransform: "uppercase" }}>
            Salir
          </button>
        </div>
      </div>

      {/* Body */}
      <div style={{ display: "flex", flex: 1, overflow: "hidden" }}>

        {/* Chat */}
        <div style={{ flex: 1, display: "flex", flexDirection: "column", borderRight: "1px solid #1a2a1a", overflow: "hidden", minWidth: 0 }}>

          {/* WhatsApp header */}
          <div style={{ background: "#0d120d", borderBottom: "1px solid #1a2a1a", padding: "10px 16px", display: "flex", alignItems: "center", gap: 10, flexShrink: 0 }}>
            <div style={{ width: 32, height: 32, borderRadius: "50%", background: "#1a3a1a", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 14, flexShrink: 0 }}>🤖</div>
            <div>
              <div style={{ fontSize: 13, color: "#e8f5e8" }}>Zer0</div>
              <div style={{ fontSize: 10, color: "#34d399" }}>en línea</div>
            </div>
            {/* Estado FSM visible en mobile */}
            <div style={{ marginLeft: "auto", display: "flex", alignItems: "center", gap: 6 }}>
              <span style={{ width: 6, height: 6, borderRadius: "50%", background: stateColor, display: "inline-block" }} />
              <span style={{ fontSize: 9, color: stateColor, letterSpacing: 1 }}>{currentState}</span>
            </div>
          </div>

          {/* Messages */}
          <div ref={chatRef}
            style={{ flex: 1, overflowY: "auto", padding: "12px 16px", display: "flex", flexDirection: "column", gap: 8, background: "#080c08" }}>
            {messages.map((msg, i) => (
              <div key={i} style={{ display: "flex", justifyContent: msg.from === "user" ? "flex-end" : "flex-start" }}>
                <div style={{
                  maxWidth: "80%",
                  background: msg.from === "user" ? "#1a3a1a" : "#0d120d",
                  border: `1px solid ${msg.from === "user" ? "#34d399" : "#1a2a1a"}`,
                  borderRadius: msg.from === "user" ? "12px 12px 2px 12px" : "12px 12px 12px 2px",
                  padding: "8px 12px",
                  fontSize: 13,
                  color: "#c8e6c8",
                  lineHeight: 1.5,
                  wordBreak: "break-word",
                }}>
                  {msg.interactive ? (
                    <InteractiveMessage msg={msg.interactive} onAction={sendMessage} />
                  ) : (
                    formatText(msg.text)
                  )}
                  <div style={{ fontSize: 9, color: "#3a5a3a", marginTop: 4, textAlign: "right" }}>{formatTime(msg.time)}</div>
                </div>
              </div>
            ))}
            {loading && (
              <div style={{ display: "flex", justifyContent: "flex-start" }}>
                <div style={{ background: "#0d120d", border: "1px solid #1a2a1a", borderRadius: "12px 12px 12px 2px", padding: "8px 14px", color: "#3a5a3a", fontSize: 13 }}>
                  escribiendo...
                </div>
              </div>
            )}
          </div>

          {/* Quick messages */}
          <div className="quick-messages" style={{ padding: "6px 12px", borderTop: "1px solid #0d120d", display: "flex", gap: 6, flexWrap: "wrap", flexShrink: 0, background: "#080c08" }}>
            {QUICK_MESSAGES.map(({ label, msg }) => (
              <button key={label} onClick={() => sendMessage(msg)}
                style={{ padding: "3px 10px", fontSize: 10, background: "transparent", border: "1px solid #1a2a1a", borderRadius: 2, color: "#4a6a4a", cursor: "pointer", fontFamily: "monospace", whiteSpace: "nowrap", flexShrink: 0 }}>
                {label}
              </button>
            ))}
          </div>

          {/* Input */}
          <div style={{ padding: "10px 16px", borderTop: "1px solid #1a2a1a", display: "flex", gap: 8, flexShrink: 0, background: "#080c08" }}>
            <input
              ref={inputRef}
              value={input}
              onChange={e => setInput(e.target.value)}
              onKeyDown={e => e.key === "Enter" && sendMessage(input)}
              placeholder="Escribí un mensaje..."
              style={{ flex: 1, background: "#0d120d", border: "1px solid #1a2a1a", borderRadius: 2, padding: "10px 12px", color: "#c8e6c8", fontSize: 13, outline: "none", fontFamily: "monospace", minWidth: 0 }}
            />
            <button onClick={() => sendMessage(input)}
              style={{ padding: "10px 16px", background: "#1a3a1a", border: "1px solid #34d399", borderRadius: 2, color: "#34d399", fontSize: 11, cursor: "pointer", letterSpacing: 1, flexShrink: 0 }}>
              ↑
            </button>
          </div>
        </div>

        {/* Panel derecho — oculto en mobile */}
        <div className="side-panel" style={{ width: 300, display: "flex", flexDirection: "column", overflow: "hidden", background: "#080c08" }}>
          <div style={{ flex: 1, overflowY: "auto", padding: "16px" }}>

            {/* OTP */}
            {otpCode && currentState === "AWAITING_OTP" && (
              <div style={{ marginBottom: 16 }}>
                <div style={{ fontSize: 9, color: "#3a5a3a", letterSpacing: 3, textTransform: "uppercase", marginBottom: 8 }}>Código OTP</div>
                <div style={{ background: "#0d120d", border: "1px solid #a78bfa", borderRadius: 2, padding: "12px 14px" }}>
                  <div style={{ fontSize: 26, color: "#a78bfa", fontWeight: 300, letterSpacing: 6, marginBottom: 8 }}>{otpCode}</div>
                  <button onClick={() => sendMessage(otpCode)}
                    style={{ width: "100%", padding: "7px", background: "#1a0d2e", border: "1px solid #a78bfa", borderRadius: 2, color: "#a78bfa", fontSize: 10, cursor: "pointer", letterSpacing: 2, textTransform: "uppercase" }}>
                    Usar código
                  </button>
                </div>
              </div>
            )}

            {/* Sesión Redis */}
            <div style={{ marginBottom: 16 }}>
              <div style={{ fontSize: 9, color: "#3a5a3a", letterSpacing: 3, textTransform: "uppercase", marginBottom: 8 }}>Sesión Redis</div>
              <div style={{ background: "#0d120d", border: "1px solid #1a2a1a", borderRadius: 2, padding: "12px 14px" }}>
                {Object.keys(ctx).length === 0 ? (
                  <div style={{ fontSize: 11, color: "#3a5a3a" }}>Sin sesión activa</div>
                ) : (
                  <pre style={{ fontSize: 10, color: "#6b9a6b", margin: 0, whiteSpace: "pre-wrap", wordBreak: "break-all", lineHeight: 1.6 }}>
                    {JSON.stringify(ctx, null, 2)}
                  </pre>
                )}
              </div>
            </div>

            {/* Transacción activa */}
            {ctx.tx_id && (
              <div>
                <div style={{ fontSize: 9, color: "#3a5a3a", letterSpacing: 3, textTransform: "uppercase", marginBottom: 8 }}>Transacción activa</div>
                <div style={{ background: "#0d120d", border: "1px solid #1a2a1a", borderRadius: 2, padding: "12px 14px", display: "flex", flexDirection: "column", gap: 6 }}>
                  {[
                    { label: "ID", value: ctx.tx_id?.slice(0, 8) + "..." },
                    { label: "Corredor", value: ctx.corridor },
                    { label: "Monto", value: ctx.source_amount ? `$${ctx.source_amount} USD` : null },
                    { label: "Recibe", value: ctx.target_amount ? `${ctx.target_amount?.toLocaleString()} ${ctx.target_currency}` : null },
                    { label: "Rate", value: ctx.quoted_rate ? `1 USD = ${ctx.quoted_rate} ${ctx.target_currency}` : null },
                    { label: "Fee", value: ctx.fee_usd ? `$${ctx.fee_usd} USD` : null },
                    { label: "Total", value: ctx.total_usd ? `$${ctx.total_usd} USD` : null },
                  ].filter(r => r.value).map(row => (
                    <div key={row.label} style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                      <div style={{ fontSize: 9, color: "#3a5a3a", letterSpacing: 1 }}>{row.label}</div>
                      <div style={{ fontSize: 11, color: "#6b9a6b", fontFamily: "monospace" }}>{row.value}</div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}